import React, { useEffect, useState, useMemo } from 'react';
import { isMobile } from 'utils/deviceHelper';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { isCcmActionAllowedStatus, isPatient } from 'utils/userTypeHelper';
import PatientNameRiskStatusBadge from 'components/atoms/badges/patientNameRiskStatusBadge';
import PatientMobileNameRiskStatusBadge from 'components/atoms/badges/patientMobileNameRiskStatusBadge';
import useUser from 'utils/useUser';
import { useHistory, useParams } from 'react-router-dom';
import ControlPanel from 'components/organisms/controlPanel';
import CcmInteractiveTime from 'components/organisms/ccmInteractiveTime';
import PatientFilter from 'components/templates/ccm/patientDetails/patientFilter';
import { downloadPDF, showResult } from 'utils';
import DownloadFileIcon from 'components/atoms/icons/downloadFileIcon';
import CarePlanEmailIcon from 'components/atoms/icons/careplanEmailIcon';
import common from 'constants/common';
import MiniNav from 'components/templates/ccm/patientDetails/miniNav';
import DropdownSection from 'components/atoms/dropdownSection';
import Questionnaire from 'components/templates/ccm/patientDetails/questionnaire';
import MedicalHistory from 'components/templates/ccm/patientDetails/medicalHistory';
import ClinicalActivity from 'components/templates/ccm/patientDetails/clinicalActivity';
import Medication from 'components/templates/ccm/patientDetails/medication';
import PatientGoals from 'components/templates/ccm/patientDetails/patientGoals';
import UserProfile from 'components/templates/ccm/patientDetails/userProfile';
import InformedConsent from 'components/templates/ccm/patientDetails/components/informedConsent';
import classnames from 'classnames';

const PatientDetails = () => {
  const [isEmailButtonLoading, setIsEmailButtonLoading] = useState(false);
  const [isDownloadButtonLoading, setIsDownloadButtonLoading] = useState(false);
  const [showMessages, setShowMessages] = useState(false);

  const setLoadingState = (type, state) => {
    if (type === 'email') {
      setIsEmailButtonLoading(state);
    } else {
      setIsDownloadButtonLoading(state);
    }
  };

  const history = useHistory();
  const { patientGuid } = useParams();
  const { userType } = useUser();
  const deviceType = useSelector((state) => state.user.deviceType);
  const patientDetailsLoading = useSelector(
    (state) => state.loading.effects.ccmPatientDetails.getPatientDetails,
  );
  const patient = useSelector((state) => state.ccmPatientDetails.patient);
  const canEditPage = isCcmActionAllowedStatus(patient?.profile?.ccmStatus);
  const patientRiskScore = useSelector(
    (state) => state.ccmPatientDetails?.questions?.score || 0,
  );
  const questionsAreLoading = useSelector(
    (state) => state.loading.effects.ccmPatientDetails.getQuestions,
  );
  const [clicked, setClicked] = useState(false);

  const {
    ccmPatientDetails: { getPatientDetails, applyDateFilter, getCarePlanPDF },
    adminPanelPatients: { getPatient },
    ccmPatients: { generatePDF },
    sms: { setTimeRange },
  } = useDispatch();

  const handleSubmit = (dates) => {
    setTimeRange(dates);
    setShowMessages(true);
    applyDateFilter(dates);
  };

  const downloadReport = async (type) => {
    setLoadingState(type, true);
    const { error, data } = await getCarePlanPDF({ patientGuid, type });
    setLoadingState(type, false);
    if (error) return showResult(error);
    if (type === 'email') {
      return showResult(error, common.submitSuccessTypes.sendReport);
    }
    if (data) {
      const reportName = `careplan-report-${patientGuid}.pdf`;
      downloadPDF(data, reportName);
    }
  };

  const isMobileDevice = useMemo(() => isMobile(deviceType), [deviceType]);

  useEffect(async () => {
    const patientDetails = await getPatientDetails(patientGuid);
    await getPatient({ patientGuid });
    if (patientDetails?.profile?.ccmStatus === 'not_enrolled') {
      showResult('Current user is not enrolled to CCM');
      history.replace('/404');
    }
  }, [patientGuid]);

  const downloadConsentFormPdf = async (e) => {
    e.preventDefault();
    const { error, data } = await generatePDF({ patientGuid });

    if (!error && data) {
      downloadPDF(data, `consent-form-${patientGuid}.pdf`);
    }
    setClicked(false);
  };

  const clickHandler = (e) => {
    setClicked(true);
    downloadConsentFormPdf(e);
  };

  return (
    <>
      {isMobileDevice && !isPatient(userType) && (
        <PatientMobileNameRiskStatusBadge
          profile={patient.profile}
          riskScore={patientRiskScore}
          patientDetailsLoading={patientDetailsLoading}
        />
      )}
      <Container className="ccm-container">
        <div className="ccm-header">
          {!isMobileDevice && !isPatient(userType) && (
            <div className="badges">
              <PatientNameRiskStatusBadge
                profile={patient.profile}
                riskScore={patientRiskScore}
                patientDetailsLoading={
                  patientDetailsLoading || questionsAreLoading
                }
              />
            </div>
          )}
          <div
            className={classnames({
              'ccm-header__informed-consent': !isMobileDevice,
              'ccm-header__informed-consent-mobile': isMobileDevice,
              'disable-block': clicked,
            })}
          >
            <InformedConsent
              clickHandler={(e) => (!clicked ? clickHandler(e) : null)}
            />
          </div>
        </div>

        <Row className="ccm-patient ccm-patient__section">
          <Col xl="1" lg="2" md="2" className="ccm-patient__aside">
            <div className="ccm-patient__mini-nav">
              <MiniNav />
            </div>
          </Col>
          <Col xl="11" lg="10" md="10" xs="12" className="ccm-patient__main">
            <Card className="card-border">
              <CardBody>
                <Row className="ccm-patient__row--centered">
                  <Col xs="12" md="8" lg="9" xl="9">
                    <PatientFilter handleSubmitTime={handleSubmit} />
                  </Col>
                  {!isPatient(userType) && (
                    <Col
                      className="download-buttons__wrapper"
                      xs="12"
                      md="4"
                      lg="3"
                      xl="3"
                    >
                      <button
                        className={classnames('download-btn', {
                          'disable-block': isEmailButtonLoading,
                        })}
                        onClick={() => downloadReport('email')}
                        data-cy="ccm-patient__email-download-report"
                      >
                        <CarePlanEmailIcon />
                        <span className="download-btn__title">
                          Email Report
                        </span>
                      </button>
                      <button
                        className={classnames('download-btn', {
                          'disable-block': isDownloadButtonLoading,
                        })}
                        onClick={() => downloadReport(null)}
                        data-cy="ccm-patient__download-report"
                      >
                        <DownloadFileIcon />
                        <span className="download-btn__title">
                          Download <br /> Care Plan
                        </span>
                      </button>
                    </Col>
                  )}
                </Row>
              </CardBody>
            </Card>

            {!isPatient(userType) && (
              <Card
                className="card-border ccm-patient__section ccm-buttons__container"
                id="control-panel"
              >
                <CardBody>
                  <Row>
                    <Col
                      xs="12"
                      md="7"
                      lg="8"
                      xl="7"
                      className="ccm-patient__control-panel"
                    >
                      <ControlPanel
                        patientGuid={patientGuid}
                        profile={patient.profile}
                        doctor={patient.doctor}
                        patientDetailsLoading={patientDetailsLoading}
                        disableControlPanelButtons={!canEditPage}
                        isCcmPanel
                      />
                    </Col>
                    <Col
                      xs="12"
                      md="5"
                      lg="4"
                      xl="5"
                      className="ccm-patient__interactive-time"
                    >
                      <CcmInteractiveTime
                        patientGuid={patientGuid}
                        ccmPatientDetails={patient}
                        patientDetailsLoading={patientDetailsLoading}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            )}

            <div className="ccm-patient__section" id="questionnaire">
              <DropdownSection title="Patient Health Questionnaire">
                <Questionnaire
                  patientGuid={patientGuid}
                  canEditPage={canEditPage}
                />
              </DropdownSection>
            </div>

            <div className="ccm-patient__section" id="medical-history">
              <MedicalHistory
                patientDetailsLoading={false}
                canEditPage={canEditPage}
              />
            </div>

            <div className="ccm-patient__section" id="clinical-activity">
              <ClinicalActivity />
            </div>

            <div className="ccm-patient__section" id="medication">
              <Medication
                patientDetailsLoading={false}
                canEditPage={canEditPage}
              />
            </div>

            <div className="ccm-patient__section" id="patient-goals">
              <PatientGoals canEditPage={canEditPage} />
            </div>

            <div className="ccm-patient__section" id="user-profile">
              <UserProfile
                patientGuid={patientGuid}
                patient={patient}
                patientDetailsLoading={patientDetailsLoading}
                showMessages={showMessages}
              />
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default PatientDetails;
